import throttle from "lodash.throttle";
import Emitter from "es6-event-emitter";

import TopBarComponent from "./../../shared/components/topBarComponent";
import CategoriesPickerComponent from "./categoriesPickerComponent";
import ReplicaReaderComponent from "./replicaReaderComponent";
import NextgenReaderComponent from "./nextgenReaderComponent";
import DownloadButtonComponent from "./../../../common/components/downloadButtonComponent";
import PrintButtonComponent from "./printButtonComponent";
import SettingsButtonComponent from "./../../shared/components/settingsButtonComponent";
import HelpButtonComponent from "./../../shared/components/helpButtonComponent";
import SidebarComponent from "./sidebarComponent";
import language from "./../../../common/dictionary";
import HorsseriesStore from "./../../../common/stores/horsseriesStore";
import APIManager from "./../../../common/utilities/apimanager";
import SliderNavigation from "./../../../common/components/sliderNavigation";
import PreferredViewerSwitcher from "./../../../common/components/preferredViewerSwitcher";
import ReadingModeButtonComponent from "./../../reader/components/readingModeButtonComponent";
import SharingLandingBannerComponent from "./../../../common/components/sharingLandingBannerComponent";

import { goToHomeFromReader } from "./../../../common/utilities/utils";
import eventBus from "../../../common/utilities/eventBus";
import ACTIONS from "../../../common/utilities/eventBusActions";

import {
  CLICKED_HOME_ICON_READER,
  CLICKED_NEXTGEN_READING_MODE_READER,
  CLICKED_REPLICA_READING_MODE_READER,
  OPENED_PUBLICATION_READER,
} from "./../../../common/analytics/analyticsevents";
import { hideLoaderAndChangeLocation } from "../../../common/utilities/location";

export default class Viewer extends Emitter {
  /**
   * @type {SharingLandingBannerComponent | null}
   */
  sharingLandingBannerComponent = null;

  constructor({
    appState,
    pubStore,
    cpStore,
    regionManager,
    paywall,
    options,
    sessionManager,
  }) {
    super();
    this._appState = appState;
    this._pubStore = pubStore;
    this._cpStore = cpStore;
    this._regionManager = regionManager;
    this._paywall = paywall;
    this._options = options;
    this._sessionManager = sessionManager;
    this._appState.on(
      "preferredViewer:changed",
      this._onPreferredViewerChanged
    );
    this._appState.on("appState:isDoublePageChanged", () => {
      this.reload();
    });

    let eventData = {
      date: this._pubStore.PublicationToLoad.PublicationDate,
      region: this._regionManager.SelectedRegion
        ? this._regionManager.SelectedRegion.Description.toLocaleLowerCase()
        : null,
    };
    this._appState.triggerEvent(OPENED_PUBLICATION_READER, eventData, true);
  }

  initComponents() {
    const topBarComponent = new TopBarComponent(this._pubStore);
    topBarComponent.on("topBar:rendered", this._renderTopBarContent.bind(this));
    topBarComponent.on("topBar:logoClicked", this._logoClicked.bind(this));
    topBarComponent.render(undefined, this._options.useCustomTooltips, true);
    this._initReader();
  }

  reload() {
    this.readerComponent.destroy();

    if (this._switchToReplica()) {
      eventBus.trigger(ACTIONS.PUBLICATION_SWITCHED, { isNextgen: false });
      $("#reader-container").removeClass("nextgen");
      this.readerComponent = new ReplicaReaderComponent(
        this._pubStore,
        this._appState,
        this._options
      );
      this.setupReplicaReaderComponentEventListeners();
    } else if (this._switchToNextgen()) {
      eventBus.trigger(ACTIONS.PUBLICATION_SWITCHED, { isNextgen: true });
      $("#reader-container").addClass("nextgen");
      this.readerComponent = new NextgenReaderComponent(
        this._pubStore,
        this._appState,
        this._options
      );
    } else {
      if (this._pubStore.isCurrentPublicationReplica()) {
        $("#reader-container").removeClass("nextgen");
        this.readerComponent = new ReplicaReaderComponent(
          this._pubStore,
          this._appState,
          this._options
        );
        this.setupReplicaReaderComponentEventListeners();
      } else {
        $("#reader-container").addClass("nextgen");
        this.readerComponent = new NextgenReaderComponent(
          this._pubStore,
          this._appState,
          this._options
        );
      }
    }

    this.sliderNavigation.destroy();
    this.sliderNavigation = null;
    this.readerComponent.on("reader:rendered", this._onReaderRendered);
    this.readerComponent.render();
    $("#app-content").removeClass("loading-pub");
    this._appState.triggerEvent(OPENED_PUBLICATION_READER);
  }

  setupReplicaReaderComponentEventListeners() {
    if (this.readerComponent) {
      this.readerComponent.on("albController:closeAlb", () => {
        this.trigger("albController:closeAlb");
      });
      this.readerComponent.on("sharingLandingBanner:loginButtonClicked", () => {
        this.trigger("sharingLandingBanner:loginButtonClicked");
      });
    }
  }

  renderLoadingLayer() {
    $("#app-content").addClass("loading-pub");
  }

  checkAlb() {
    if (this.readerComponent) {
      this.readerComponent.checkAlb();
    }
  }

  //////////

  _renderTopBarContent() {
    if (this._options.replicaReadingModeToggle.desktop) {
      const readingModeButtonComponent = new ReadingModeButtonComponent(
        this._appState,
        this._pubStore,
        this._options
      );
      readingModeButtonComponent.render();
    }
    const categoriesPickerComponent = new CategoriesPickerComponent(
      this._pubStore,
      this._appState,
      this._options
    );
    categoriesPickerComponent.render();

    this.downloadButtonComponent = new DownloadButtonComponent(
      this._pubStore,
      this._appState,
      this._options,
      this._sessionManager
    );
    this.downloadButtonComponent.render();

    const section = document.querySelector(
      ".top-bar-container .top-bar-access"
    );
    const printButtonComponent = new PrintButtonComponent(
      section,
      this._pubStore,
      this._appState,
      this._options,
      this._sessionManager
    );
    printButtonComponent.render();

    if (this._options.settings.turnOnSettings) {
      const settingsButtonComponent = new SettingsButtonComponent(
        this._appState,
        this._regionManager,
        this._sessionManager,
        this._paywall,
        this._options.settings
      );
      settingsButtonComponent.on("settingComponent:onRegionChanged", () => {
        this.trigger("viewer:onRegionChanged");
      });
      settingsButtonComponent.on("settingComponent:onLoginChanged", () => {
        this.trigger("viewer:onLoginChanged");
      });
      settingsButtonComponent.render();
    }

    if (this._options.help && this._options.help.turnOnHeaderHelp) {
      const helpButtonComponent = new HelpButtonComponent(
        this._appState,
        this._regionManager,
        this._sessionManager,
        this._paywall,
        this._options.settings
      );
      helpButtonComponent.render();
    }

    if (this._options.displayViewerOptions) {
      const preferredViewerComponent = new PreferredViewerSwitcher(
        this._appState,
        document.getElementById("preferred-viewer-wrapper"),
        "icons",
        this._options
      );
      preferredViewerComponent.on(
        "readingModeSwitcher:clicked",
        this._onReadingModeChanged
      );
      preferredViewerComponent.render();
    }
  }

  _initReader() {
    if (this._pubStore.isCurrentPublicationReplica()) {
      $("#reader-container").removeClass("nextgen");
      this.readerComponent = new ReplicaReaderComponent(
        this._pubStore,
        this._appState,
        this._options
      );
      this.setupReplicaReaderComponentEventListeners();
    } else {
      $("#reader-container").addClass("nextgen");
      this.readerComponent = new NextgenReaderComponent(
        this._pubStore,
        this._appState,
        this._options
      );
    }

    this.readerComponent.on("reader:resized", this._onWidowResize.bind(this));
    this.readerComponent.on("reader:rendered", this._onReaderRendered);
    this.readerComponent.render();

    if (this._options.desktopReader.readerEnableHorsseries) {
      this._initHorsSeries();
    } else {
      this._initSidebar();
    }
    window.addEventListener(
      "resize",
      throttle(this._onWidowResize.bind(this), 100)
    );
    const readerContainer = document.querySelector("#reader-container");
    if (readerContainer) {
      readerContainer.addEventListener("click", () => {
        this._appState.trigger("readerContainer:clicked");
        const shareMenu = document.querySelectorAll(".sharing-menu");
        shareMenu.forEach((item) => {
          if (!item.classList.contains("hidden")) {
            item.classList.add("hidden");
          }
        });
      });
    }
  }

  _initHorsSeries() {
    const horsSeriesStore = new HorsseriesStore(
      APIManager,
      this._appState,
      this._cpStore.additionalDownloadTokens,
      this._options.sizes.covers,
      this._options.horsserie,
      language.moment,
      this._options.dateFormat
    );
    horsSeriesStore.on("horsseries:loaded", () => {
      this.horsseries = horsSeriesStore.getHorsseries() || [];
      this._initSidebar();
    });
    horsSeriesStore.loadHorsseries();
  }

  _initSidebar() {
    this.sidebarComponent = new SidebarComponent(
      this._appState,
      this._pubStore,
      this._setSidebarPublications(),
      this._options
    );
    this.sidebarComponent.render();
    this.sidebarComponent.on("sidebar:publicationChanged", (data) => {
      this.trigger("viewer:publicationChanged", data);
    });
  }

  _logoClicked() {
    this._appState.triggerEvent(CLICKED_HOME_ICON_READER);
    if (this._options.redirectReaderFromIframe) {
      window.top.location = this._options.returnToIframeUrl;
    } else if (this._options.homeRedirectionLink) {
      hideLoaderAndChangeLocation(this._options.homeRedirectionLink, window);
    } else {
      goToHomeFromReader();
    }
  }

  _onWidowResize() {
    if (this.sliderNavigation) this.sliderNavigation.resize();
  }

  /**
   * @param {"replica" | "nextgen"} type
   */
  _onReaderRendered = (type) => {
    $("#app-content").removeClass("loading");
    this.sliderNavigation = new SliderNavigation(
      this._appState,
      this._pubStore.CPackagePages,
      this._options.slidernavigation
    );
    this.sliderNavigation.load();
    if (
      this._appState.socialSharing &&
      this._options.sharingLandingBanner.enabled
    ) {
      this._setupSharingLandingBanner(type);
    }
    this.readerComponent.off("reader:rendered", this._onReaderRendered);
  };

  /**
   * @param {"replica" | "nextgen"} type
   */
  _setupSharingLandingBanner(type) {
    const selector = type === "nextgen" ? "#app-container" : ".alb-container";
    this.sharingLandingBannerComponent = new SharingLandingBannerComponent({
      DOMContainer: document.querySelector(selector),
      signupLink: this._options.sharingLandingBanner.signupLink,
    });
    this.sharingLandingBannerComponent.on(
      "sharingLandingBanner:loginButtonClicked",
      () => {
        this.trigger("sharingLandingBanner:loginButtonClicked");
      }
    );
  }

  _switchToReplica() {
    return (
      this.readerComponent.constructor.componentName ===
        "NextgenReaderComponent" && this._pubStore.isCurrentPublicationReplica()
    );
  }

  _switchToNextgen() {
    return (
      this.readerComponent.constructor.componentName ===
        "ReplicaReaderComponent" &&
      !this._pubStore.isCurrentPublicationReplica()
    );
  }

  _setSidebarPublications() {
    const mainPublication = this._pubStore.getPublicationByType(
      this._appState.preferredViewer
    );
    const horsseries = this.horsseries || [];

    return [...[mainPublication], ...this._cpStore.supplements, ...horsseries];
  }

  _onPreferredViewerChanged = () => {
    this.sidebarComponent.render(this._setSidebarPublications());
  };

  _onReadingModeChanged = (readingModeType) => {
    if (readingModeType === "nextgen") {
      this._appState.triggerEvent(CLICKED_NEXTGEN_READING_MODE_READER);
    } else {
      this._appState.triggerEvent(CLICKED_REPLICA_READING_MODE_READER);
    }
  };
}



// WEBPACK FOOTER //
// ./src/web/reader/components/viewer.js